import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'motion/react';
import { Toaster, toast } from 'sonner';
import {
  Upload, CheckCircle2, AlertCircle, Loader2, Download,
  RefreshCw, Camera, Maximize2, Image as ImageIcon, ArrowRight,
  ShieldCheck, LogIn, LogOut, User as UserIcon, XCircle,
  Settings2, Check, Plus, Share2, FileImage, Edit3, Trash2
} from 'lucide-react';
import {
  auth, db, storage, googleProvider,
  signInWithPopup, signOut, onAuthStateChanged,
  doc, setDoc, getDoc, collection, onSnapshot,
  updateDoc, deleteDoc, serverTimestamp,
  ref, uploadString, getDownloadURL
} from './firebase';

// ── Utility ────────────────────────────────────────────────────────────────────
function cn(...classes) { return classes.filter(Boolean).join(' '); }
// Elapsed Timer Component
function ElapsedTimer({ isRunning }) {
  const [seconds, setSeconds] = React.useState(0);

  React.useEffect(() => {
    setSeconds(0);
    if (!isRunning) return;
    const interval = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isRunning]);

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const display = mins > 0
    ? mins + 'm ' + secs.toString().padStart(2, '0') + 's'
    : seconds + 's';

  const color = seconds < 20 ? 'text-green-600' : seconds < 45 ? 'text-yellow-600' : 'text-red-500';
  const dotColor = seconds < 20 ? 'bg-green-500' : seconds < 45 ? 'bg-yellow-500' : 'bg-red-500';

  if (!isRunning && seconds === 0) return null;

  return (
    <div className={"flex items-center gap-1.5 font-mono text-[10px] " + color}>
      <div className={"w-1.5 h-1.5 rounded-full animate-pulse " + dotColor} />
      {display}
      {seconds >= 45 && <span className="opacity-60"> &mdash; trying fallback model...</span>}
      {seconds >= 20 && seconds < 45 && <span className="opacity-60"> &mdash; please wait...</span>}
    </div>
  );
}



// ── Config ─────────────────────────────────────────────────────────────────────
const API_URL = 'https://asia-south1-cs-poc-olb06ddayhymljadnl0sfwy.cloudfunctions.net/amazon-optimizer-api';
const MAX_BATCH = 10;

// ── Feature Icons Map (AI picks from these) ────────────────────────────────────
const getIconForFeature = (featureText) => {
  const lower = featureText.toLowerCase();
  if (lower.includes('breath'))        return '🌬️';
  if (lower.includes('soft'))          return '☁️';
  if (lower.includes('wash') || lower.includes('care')) return '🫧';
  if (lower.includes('safe') || lower.includes('non-toxic')) return '🛡️';
  if (lower.includes('organic') || lower.includes('natural')) return '🌿';
  if (lower.includes('cotton'))        return '🌸';
  if (lower.includes('light'))         return '🪶';
  if (lower.includes('comfort'))       return '😊';
  if (lower.includes('color') || lower.includes('vibrant')) return '🎨';
  if (lower.includes('premium') || lower.includes('quality')) return '⭐';
  if (lower.includes('eco') || lower.includes('recycl')) return '♻️';
  if (lower.includes('allerg'))        return '🌼';
  if (lower.includes('water'))         return '💧';
  if (lower.includes('durable') || lower.includes('strong')) return '💪';
  return '✨';
};

// ── Lifestyle Templates ────────────────────────────────────────────────────────
const LIFESTYLE_TEMPLATES = (productName, description) => [
  {
    id: 'professional', label: 'Standard Professional',
    prompt: `Generate a high-quality, professional lifestyle image for this product: ${productName}. The product is described as: ${description}. Requirements: 1. The product must be clearly visible and look natural in the environment. 2. The lighting should be soft and inviting. 3. The overall mood should be aspirational and high-end. 4. Maintain the original colors and textures of the product. 5. Ensure the final image is sharp and professional.`
  },
  {
    id: 'minimalist', label: 'Modern Minimalist',
    prompt: `Generate a modern, minimalist lifestyle image for ${productName}. Set the product in a clean, contemporary space with neutral tones and sharp architectural lines. Focus on elegance and simplicity. The product should be the hero of the shot.`
  },
  {
    id: 'outdoor', label: 'Natural / Outdoor',
    prompt: `Generate a lifestyle image for ${productName} in a natural, outdoor setting. Use soft morning sunlight, natural textures like wood or stone, and organic elements. The environment should feel fresh, authentic, and vibrant.`
  },
  { id: 'custom', label: 'Custom Prompt', prompt: '' }
];

// ── History Side Panel ─────────────────────────────────────────────────────────────────────────────
function HistoryPanel({ isOpen, onClose, items, onSelectItem }) {
  const formatDate = (createdAt) => {
    if (!createdAt) return '';
    // Handle Firestore timestamp
    const date = createdAt.seconds
      ? new Date(createdAt.seconds * 1000)
      : new Date(createdAt);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusLabel = (item) => {
    if (item.state === 'COMPLETED')           return { label: 'Completed',  color: 'bg-green-500' };
    if (item.state === 'REVIEWING')           return { label: 'In Review',  color: 'bg-blue-500' };
    if (item.state === 'LIFESTYLE_REVIEWING') return { label: 'Lifestyle',  color: 'bg-purple-500' };
    if (item.state === 'ERROR')               return { label: 'Error',      color: 'bg-red-500' };
    if (item.state === 'CANCELLED')           return { label: 'Cancelled',  color: 'bg-gray-400' };
    return                                           { label: item.state,   color: 'bg-gray-300' };
  };

  const getAssetCount = (item) => {
    let count = 0;
    if (item.processedImage) count++;
    if (item.lifestyleImage) count++;
    return count;
  };

  // Sort items newest first
  const sortedItems = [...items].sort((a, b) => {
    const tA = a.createdAt?.seconds || (a.createdAt instanceof Date ? a.createdAt.getTime() / 1000 : 0);
    const tB = b.createdAt?.seconds || (b.createdAt instanceof Date ? b.createdAt.getTime() / 1000 : 0);
    return tB - tA;
  });

  const completedItems  = sortedItems.filter(i => ['COMPLETED', 'REVIEWING', 'LIFESTYLE_REVIEWING'].includes(i.state));
  const processingItems = sortedItems.filter(i => ['ANALYZING', 'EDITING', 'LIFESTYLE_GENERATING', 'IDLE'].includes(i.state));
  const errorItems      = sortedItems.filter(i => ['ERROR', 'CANCELLED'].includes(i.state));

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/30 z-40"
          />
        )}
      </AnimatePresence>

      {/* Side Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-96 bg-[#E4E3E0] border-l border-[#141414] z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="p-5 border-b border-[#141414] flex justify-between items-center bg-white/50">
              <div>
                <h2 className="font-serif italic text-xl">History</h2>
                <p className="text-[9px] font-mono uppercase opacity-50 mt-0.5">
                  {items.length} images · {completedItems.length} completed
                </p>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-white/50 rounded">
                <XCircle className="w-5 h-5 opacity-50" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">

              {items.length === 0 && (
                <div className="text-center py-16 space-y-3">
                  <ImageIcon className="w-10 h-10 mx-auto opacity-20" />
                  <p className="text-[10px] font-mono uppercase opacity-40">No history yet</p>
                  <p className="text-[9px] font-mono opacity-30">Process images to see them here</p>
                </div>
              )}

              {/* Completed */}
              {completedItems.length > 0 && (
                <section className="space-y-2">
                  <p className="text-[9px] font-mono uppercase tracking-widest opacity-50">
                    Completed ({completedItems.length})
                  </p>
                  {completedItems.map((item) => (
                    <HistoryCard
                      key={item.id}
                      item={item}
                      status={getStatusLabel(item)}
                      assetCount={getAssetCount(item)}
                      formattedDate={formatDate(item.createdAt)}
                      onClick={() => { onSelectItem(item); onClose(); }}
                    />
                  ))}
                </section>
              )}

              {/* Processing */}
              {processingItems.length > 0 && (
                <section className="space-y-2">
                  <p className="text-[9px] font-mono uppercase tracking-widest opacity-50">
                    In Progress ({processingItems.length})
                  </p>
                  {processingItems.map((item) => (
                    <HistoryCard
                      key={item.id}
                      item={item}
                      status={getStatusLabel(item)}
                      assetCount={getAssetCount(item)}
                      formattedDate={formatDate(item.createdAt)}
                      onClick={() => { onSelectItem(item); onClose(); }}
                    />
                  ))}
                </section>
              )}

              {/* Errors */}
              {errorItems.length > 0 && (
                <section className="space-y-2">
                  <p className="text-[9px] font-mono uppercase tracking-widest opacity-50">
                    Failed ({errorItems.length})
                  </p>
                  {errorItems.map((item) => (
                    <HistoryCard
                      key={item.id}
                      item={item}
                      status={getStatusLabel(item)}
                      assetCount={getAssetCount(item)}
                      formattedDate={formatDate(item.createdAt)}
                      onClick={() => { onSelectItem(item); onClose(); }}
                    />
                  ))}
                </section>
              )}

            </div>

            {/* Footer */}
            <div className="p-4 border-t border-[#141414] bg-white/30">
              <p className="text-[8px] font-mono uppercase opacity-40 text-center">
                History synced across devices via Firestore
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ── History Card ──────────────────────────────────────────────────────────────
function HistoryCard({ item, status, assetCount, formattedDate, onClick }) {
  const displayImage = item.processedImage || item.originalImage;

  const handleDownload = (e, type) => {
    e.stopPropagation();
    const image = type === 'lifestyle' ? item.lifestyleImage : item.processedImage;
    if (!image) return;
    const link = document.createElement('a');
    link.href = image;
    link.download = type + '-' + (item.analysis?.productName || 'product').replace(/\s+/g, '-').toLowerCase() + '.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      onClick={onClick}
      className="group bg-white/60 border border-[#141414]/20 hover:border-[#141414] hover:shadow-[2px_2px_0px_#141414] transition-all cursor-pointer"
    >
      <div className="flex gap-3 p-3">
        {/* Thumbnail */}
        <div className="w-16 h-16 flex-shrink-0 bg-white border border-[#141414]/10 overflow-hidden">
          {displayImage
            ? <img src={displayImage} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center opacity-20">
                <ImageIcon className="w-5 h-5" />
              </div>
          }
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-[10px] font-mono font-bold truncate">
            {item.analysis?.productName || 'Processing...'}
          </p>
          <p className="text-[8px] font-mono opacity-50">{formattedDate}</p>

          <div className="flex items-center gap-2">
            {/* Status badge */}
            <div className="flex items-center gap-1">
              <div className={"w-1.5 h-1.5 rounded-full " + status.color} />
              <span className="text-[8px] font-mono uppercase opacity-60">{status.label}</span>
            </div>

            {/* Asset count */}
            {assetCount > 0 && (
              <span className="text-[8px] font-mono opacity-40">
                {assetCount} asset{assetCount > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Asset thumbnails row */}
          {(item.processedImage || item.lifestyleImage) && (
            <div className="flex gap-1 pt-1">
              {item.processedImage && (
                <div className="relative group/thumb">
                  <img src={item.processedImage} alt="Studio"
                    className="w-8 h-8 object-cover border border-[#141414]/20" />
                  <button
                    onClick={(e) => handleDownload(e, 'studio')}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <Download className="w-3 h-3 text-white" />
                  </button>
                </div>
              )}
              {item.lifestyleImage && (
                <div className="relative group/thumb">
                  <img src={item.lifestyleImage} alt="Lifestyle"
                    className="w-8 h-8 object-cover border border-[#141414]/20" />
                  <button
                    onClick={(e) => handleDownload(e, 'lifestyle')}
                    className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <Download className="w-3 h-3 text-white" />
                  </button>
                </div>
              )}
              <p className="text-[7px] font-mono opacity-30 self-end pb-0.5 ml-1">
                hover to download
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Brand Setup Modal ──────────────────────────────────────────────────────────
function BrandSetupModal({ user, existingBrand, onSave, onClose }) {
  const [brandName, setBrandName]   = useState(existingBrand?.brandName || '');
  const [subtext, setSubtext]       = useState(existingBrand?.subtext || '');
  const [logoPreview, setLogoPreview] = useState(existingBrand?.logoUrl || null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [ctaText, setCtaText]       = useState(existingBrand?.ctaText || 'DM to Order');
  const [marketplaceText, setMarketplaceText] = useState(existingBrand?.marketplaceText || 'Also available on amazon.in');
  const [saving, setSaving]         = useState(false);

  const onLogoDrop = useCallback((files) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
      setLogoBase64(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } = useDropzone({
    onDrop: onLogoDrop, accept: { 'image/*': [] }, multiple: false
  });

  const handleSave = async () => {
    if (!brandName.trim()) { toast.error('Brand name is required.'); return; }
    if (!logoPreview)      { toast.error('Please upload a logo.'); return; }
    setSaving(true);
    try {
      let logoUrl = existingBrand?.logoUrl || null;
      // Upload new logo to Firebase Storage if changed
      if (logoBase64) {
        const logoRef = ref(storage, `logos/${user.uid}/brand-logo`);
        await uploadString(logoRef, logoBase64, 'data_url');
        logoUrl = await getDownloadURL(logoRef);
      }
      const brandData = { brandName, subtext, logoUrl, ctaText, marketplaceText, updatedAt: serverTimestamp() };
      await updateDoc(doc(db, 'users', user.uid), { brand: brandData });
      toast.success('Brand settings saved!');
      onSave({ ...brandData, logoUrl });
    } catch (err) {
      console.error('Brand save error:', err);
      toast.error('Failed to save brand settings.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-[#141414] flex justify-between items-center">
          <div>
            <h2 className="font-serif italic text-xl">Brand Setup</h2>
            <p className="text-[9px] font-mono uppercase opacity-50 mt-0.5">Configure your brand identity</p>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1 hover:bg-white/50">
              <XCircle className="w-4 h-4 opacity-50" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-5">
          {/* Logo Upload */}
          <div className="space-y-2">
            <p className="text-[9px] uppercase font-mono opacity-50">Brand Logo *</p>
            <div
              {...getLogoRootProps()}
              className="border border-dashed border-[#141414] p-4 text-center cursor-pointer hover:bg-white/30 transition-colors"
            >
              <input {...getLogoInputProps()} />
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="h-16 mx-auto object-contain" />
              ) : (
                <div className="space-y-1">
                  <Upload className="w-5 h-5 mx-auto opacity-30" />
                  <p className="text-[9px] font-mono uppercase opacity-50">Drop logo or click to upload</p>
                </div>
              )}
            </div>
            {logoPreview && (
              <button
                onClick={() => { setLogoPreview(null); setLogoBase64(null); }}
                className="text-[8px] font-mono uppercase opacity-50 hover:opacity-100 flex items-center gap-1"
              >
                <Trash2 className="w-2.5 h-2.5" /> Remove logo
              </button>
            )}
          </div>

          {/* Brand Name */}
          <div className="space-y-1.5">
            <p className="text-[9px] uppercase font-mono opacity-50">Brand Name *</p>
            <input
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="e.g. Tra La La"
              className="w-full p-2.5 border border-[#141414] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#141414] bg-transparent"
            />
          </div>

          {/* Subtext */}
          <div className="space-y-1.5">
            <p className="text-[9px] uppercase font-mono opacity-50">Brand Subtext</p>
            <input
              value={subtext}
              onChange={(e) => setSubtext(e.target.value)}
              placeholder="e.g. Babies & Kids"
              className="w-full p-2.5 border border-[#141414] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#141414] bg-transparent"
            />
          </div>

          {/* CTA Text */}
          <div className="space-y-1.5">
            <p className="text-[9px] uppercase font-mono opacity-50">Call to Action Text</p>
            <input
              value={ctaText}
              onChange={(e) => setCtaText(e.target.value)}
              placeholder="e.g. DM to Order"
              className="w-full p-2.5 border border-[#141414] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#141414] bg-transparent"
            />
          </div>

          {/* Marketplace Text */}
          <div className="space-y-1.5">
            <p className="text-[9px] uppercase font-mono opacity-50">Marketplace Text</p>
            <input
              value={marketplaceText}
              onChange={(e) => setMarketplaceText(e.target.value)}
              placeholder="e.g. Also available on amazon.in"
              className="w-full p-2.5 border border-[#141414] text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#141414] bg-transparent"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#141414] text-[#E4E3E0] py-3 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</> : 'Save Brand Settings'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── Instagram Post (Hybrid: Gemini image + Canvas bottom bar + Logo) ────────
function InstagramPostGenerator({ item, brand, onClose }) {
  const canvasRef = React.useRef(null);
  const [productName, setProductName] = React.useState(item.analysis?.productName || '');
  const [tagline, setTagline]         = React.useState('Premium quality');
  const [ctaText, setCtaText]         = React.useState(brand?.ctaText || 'DM to Order');
  const [marketplace, setMarketplace] = React.useState('Also available on');
  const [generatedImage, setGeneratedImage] = React.useState(null);
  const [finalImage, setFinalImage]   = React.useState(null);
  const [generating, setGenerating]   = React.useState(false);
  const [elapsed, setElapsed]         = React.useState(0);

  const loadImage = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

  // Draw bottom bar + logo on top of Gemini image
  const applyOverlays = React.useCallback(async (geminiDataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas || !geminiDataUrl) return geminiDataUrl;

    const ctx = canvas.getContext('2d');

    // 1. Load Gemini image and use ITS natural dimensions — no forced resize
    const baseImg = await loadImage(geminiDataUrl);
    if (!baseImg) return geminiDataUrl;

    const W = baseImg.naturalWidth  || baseImg.width;
    const H = baseImg.naturalHeight || baseImg.height;
    canvas.width  = W;
    canvas.height = H;

    // Draw full image at natural size
    ctx.drawImage(baseImg, 0, 0, W, H);

    // 2. Bottom bar — height proportional to image height (~14%)
    const barH = Math.round(H * 0.14);
    const barY = H - barH; // sits AT BOTTOM of image, overlaid on it

    ctx.fillStyle = 'rgba(245, 243, 240, 0.90)';
    ctx.fillRect(0, barY, W, barH);

    // Top border line
    ctx.strokeStyle = 'rgba(150, 145, 140, 0.7)';
    ctx.lineWidth = Math.round(W * 0.0015);
    ctx.beginPath();
    ctx.moveTo(0, barY);
    ctx.lineTo(W, barY);
    ctx.stroke();

    // Scale fonts proportionally to image width
    const scale = W / 1080;
    const col2X = W * 0.44;
    const col3X = W * 0.72;
    const midY  = barY + barH / 2;

    // ── Col 1: Product name + tagline ──
    ctx.fillStyle = '#1a1a1a';
    ctx.font = 'bold ' + Math.round(34 * scale) + 'px Arial';
    ctx.textAlign = 'left';
    let pName = productName;
    while (ctx.measureText(pName).width > col2X - 50 * scale && pName.length > 8) {
      pName = pName.substring(0, pName.length - 4) + '...';
    }
    ctx.fillText(pName, 30 * scale, midY - 10 * scale);

    ctx.fillStyle = '#666666';
    ctx.font = Math.round(22 * scale) + 'px Arial';
    ctx.fillText(tagline, 30 * scale, midY + 24 * scale);

    // Divider 1
    ctx.strokeStyle = 'rgba(150,145,140,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(col2X - 12 * scale, barY + 22 * scale);
    ctx.lineTo(col2X - 12 * scale, H - 22 * scale);
    ctx.stroke();

    // ── Col 2: CTA ──
    ctx.fillStyle = '#1a1a1a';
    ctx.font = 'bold ' + Math.round(32 * scale) + 'px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(ctaText, col2X, midY - 10 * scale);

    ctx.fillStyle = '#666666';
    ctx.font = Math.round(20 * scale) + 'px Arial';
    ctx.fillText(marketplace, col2X, midY + 24 * scale);

    // Divider 2
    ctx.strokeStyle = 'rgba(150,145,140,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(col3X - 12 * scale, barY + 22 * scale);
    ctx.lineTo(col3X - 12 * scale, H - 22 * scale);
    ctx.stroke();

    // ── Col 3: Amazon logo from internet ──
    const amzCX = col3X + (W - col3X) / 2;
    const amzTextY = midY - 8 * scale;

    // Try loading Amazon logo from CDN
    const amazonLogo = await loadImage('https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo_SVG.svg')
      .catch(() => null);

    if (amazonLogo) {
      // Draw actual Amazon logo image
      const logoW = Math.round(120 * scale);
      const logoH = Math.round(36 * scale);
      ctx.drawImage(amazonLogo, amzCX - logoW / 2, amzTextY - logoH / 2, logoW, logoH);
      // .in text below
      ctx.fillStyle = '#1a1a1a';
      ctx.font = Math.round(18 * scale) + 'px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('.in', amzCX, amzTextY + logoH / 2 + 18 * scale);
    } else {
      // Fallback: draw Amazon text + orange smile
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold ' + Math.round(30 * scale) + 'px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('amazon', amzCX, amzTextY);
      ctx.font = Math.round(20 * scale) + 'px Arial';
      ctx.fillText('.in', amzCX, amzTextY + 30 * scale);

      // Orange smile
      ctx.strokeStyle = '#FF9900';
      ctx.lineWidth = 4 * scale;
      ctx.lineCap = 'round';
      const arcR = 40 * scale;
      ctx.beginPath();
      ctx.arc(amzCX, amzTextY + 4 * scale, arcR, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();

      // Arrow tip
      const arrowX = amzCX + arcR * Math.cos(0.85 * Math.PI);
      const arrowY = amzTextY + 4 * scale + arcR * Math.sin(0.85 * Math.PI);
      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(arrowX + 9 * scale, arrowY - 5 * scale);
      ctx.strokeStyle = '#FF9900';
      ctx.stroke();
    }

    // 3. Logo top-left — 400x400 as requested
    if (brand?.logoUrl) {
      try {
        const resp = await fetch(brand.logoUrl);
        const blob = await resp.blob();
        const logoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

        const logoImg = await loadImage(logoDataUrl);
        if (logoImg) {
          // Strip black and white backgrounds
          const off = document.createElement('canvas');
          off.width  = logoImg.width;
          off.height = logoImg.height;
          const offCtx = off.getContext('2d');
          offCtx.drawImage(logoImg, 0, 0);
          const imgData = offCtx.getImageData(0, 0, off.width, off.height);
          const d = imgData.data;
          for (let i = 0; i < d.length; i += 4) {
            const r = d[i], g = d[i+1], b = d[i+2];
            if ((r < 30 && g < 30 && b < 30) || (r > 225 && g > 225 && b > 225)) {
              d[i+3] = 0;
            }
          }
          offCtx.putImageData(imgData, 0, 0);

          // Draw at 400x400 as requested
          const logoSize = Math.round(400 * scale);
          ctx.drawImage(off, 20 * scale, 20 * scale, logoSize, logoSize);
        }
      } catch (e) {
        ctx.fillStyle = '#141414';
        ctx.font = 'bold ' + Math.round(32 * scale) + 'px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(brand?.brandName || '', 24 * scale, 60 * scale);
      }
    }

    return canvas.toDataURL('image/png');
  }, [brand, productName, tagline, ctaText, marketplace]);

  // Re-apply overlays when user edits text (instant, no AI call)
  const refreshOverlays = async () => {
    if (!generatedImage) {
      toast.error('Generate the post first.');
      return;
    }
    const result = await applyOverlays(generatedImage);
    setFinalImage(result);
    toast.success('Preview updated!');
  };

  const generatePost = async () => {
    // Use lifestyle image directly — no need to call AI again
    // Gemini already enhanced the lifestyle image in the previous step
    const mainImage = item.lifestyleImage || item.processedImage;
    if (!mainImage) { toast.error('No lifestyle image found. Generate a lifestyle image first.'); return; }

    setGenerating(true);
    setGeneratedImage(null);
    setFinalImage(null);
    setElapsed(0);

    const timerInterval = setInterval(() => setElapsed(e => e + 1), 1000);

    try {
      // Save the lifestyle image as base
      setGeneratedImage(mainImage);

      // Apply canvas overlays directly on lifestyle image — no AI call needed
      const result = await applyOverlays(mainImage);
      setFinalImage(result);
      toast.success('Instagram post created!');
    } catch (err) {
      toast.error('Failed to create post: ' + err.message);
    } finally {
      clearInterval(timerInterval);
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!finalImage) return;
    const link = document.createElement('a');
    link.download = 'instagram-post-' + (productName || 'product').replace(/\s+/g, '-').toLowerCase() + '.png';
    link.href = finalImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Instagram post downloaded!');
  };

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const elapsedDisplay = mins > 0 ? mins + 'm ' + String(secs).padStart(2, '0') + 's' : elapsed + 's';
  const timerColor = elapsed < 20 ? 'text-green-600' : elapsed < 45 ? 'text-yellow-600' : 'text-red-500';
  const dotColor   = elapsed < 20 ? 'bg-green-500'  : elapsed < 45 ? 'bg-yellow-500'  : 'bg-red-500';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl max-h-[95vh] overflow-y-auto">

        <div className="p-5 border-b border-[#141414] flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5" />
            <div>
              <h2 className="font-serif italic text-xl">Instagram Post</h2>
              <p className="text-[9px] font-mono uppercase opacity-50">AI Photo + Canvas Bar + Real Logo</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/50">
            <XCircle className="w-5 h-5 opacity-50" />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-mono text-[10px] uppercase tracking-widest opacity-50">Post Details</h3>

            {[
              { label: 'Product Name', val: productName, set: setProductName, hint: 'Shown in bottom bar' },
              { label: 'Tagline',      val: tagline,     set: setTagline,     hint: 'Below product name' },
              { label: 'CTA Text',     val: ctaText,     set: setCtaText,     hint: 'e.g. DM to Order' },
              { label: 'Marketplace',  val: marketplace, set: setMarketplace, hint: 'e.g. Also available on' },
            ].map(({ label, val, set, hint }) => (
              <div key={label} className="space-y-1">
                <p className="text-[9px] uppercase font-mono opacity-50">{label}</p>
                <input value={val} onChange={(e) => set(e.target.value)}
                  className="w-full p-2.5 border border-[#141414] text-xs font-mono focus:outline-none bg-transparent" />
                <p className="text-[8px] font-mono opacity-30">{hint}</p>
              </div>
            ))}

            {brand && (
              <div className="p-3 bg-white/40 border border-[#141414]/20 flex items-center gap-3">
                {brand.logoUrl && <img src={brand.logoUrl} alt="Logo" className="h-8 object-contain" />}
                <div>
                  <p className="text-[9px] uppercase font-mono opacity-50">Logo placed top-left by canvas</p>
                  <p className="text-xs font-mono">{brand.brandName}</p>
                </div>
              </div>
            )}

            <button onClick={generatePost} disabled={generating}
              className="w-full bg-[#141414] text-[#E4E3E0] py-3 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 mt-2">
              {generating
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                : <><Share2 className="w-3 h-3" /> Generate with AI</>}
            </button>

            {generating && elapsed > 0 && (
              <div className={"flex items-center gap-1.5 font-mono text-[10px] justify-center " + timerColor}>
                <div className={"w-1.5 h-1.5 rounded-full animate-pulse " + dotColor} />
                {elapsedDisplay}
                {elapsed >= 45 && <span className="opacity-60"> — trying fallback...</span>}
                {elapsed >= 20 && elapsed < 45 && <span className="opacity-60"> — Gemini designing...</span>}
              </div>
            )}

            {finalImage && !generating && (
              <>
                <button onClick={refreshOverlays}
                  className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-white/50 flex items-center justify-center gap-2">
                  <RefreshCw className="w-3 h-3" /> Update Bar Text
                </button>
                <button onClick={generatePost}
                  className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-white/50 flex items-center justify-center gap-2">
                  <Share2 className="w-3 h-3" /> Regenerate Photo
                </button>
                <button onClick={handleDownload}
                  className="w-full bg-[#141414] text-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 flex items-center justify-center gap-2">
                  <Download className="w-3 h-3" /> Download Post
                </button>
              </>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-[9px] uppercase font-mono opacity-50">
              {finalImage ? 'Preview — Bottom bar & logo added by canvas' : 'Preview appears after generation'}
            </p>
            <div className="border border-[#141414] overflow-hidden bg-white/50 aspect-square flex items-center justify-center">
              {finalImage ? (
                <img src={finalImage} alt="Instagram Post" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center space-y-3 p-8">
                  {generating ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-30" />
                      <p className="text-[9px] font-mono uppercase opacity-40">Gemini styling your photo...</p>
                      <p className="text-[8px] font-mono opacity-30">Bottom bar + logo added automatically after</p>
                    </>
                  ) : (
                    <>
                      <Share2 className="w-8 h-8 mx-auto opacity-20" />
                      <p className="text-[9px] font-mono uppercase opacity-40">Fill details and click Generate</p>
                    </>
                  )}
                </div>
              )}
            </div>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {finalImage && (
              <p className="text-[9px] font-mono opacity-40 text-center">
                Edit text above → click Update Bar Text (instant) or Regenerate Photo (AI)
              </p>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── Infographic Canvas ─────────────────────────────────────────────────────────────────────────────
function InfographicGenerator({ item, brand, onClose }) {
  const canvasRef = React.useRef(null);
  const [productName, setProductName] = React.useState(item.analysis?.productName || '');
  const [tagline, setTagline]         = React.useState('Premium quality');
  const [ctaText, setCtaText]         = React.useState(brand?.ctaText || 'DM to Order');
  const [ctaSubtext, setCtaSubtext]   = React.useState('Also available on');
  const [features, setFeatures]       = React.useState(['', '', '', '']);
  const [rendered, setRendered]       = React.useState(false);

  const updateFeature = (idx, val) => {
    const updated = [...features];
    updated[idx] = val;
    setFeatures(updated);
  };

  const loadImage = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

  const drawInfographic = React.useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const S = 1080;
    canvas.width = S;
    canvas.height = S;
    setRendered(false);

    const productImage = item.processedImage || item.originalImage;
    if (!productImage) return;

    const draw = async () => {
      // 1. Full bleed product image — cover fill
      const prodImg = await loadImage(productImage);
      if (!prodImg) return;

      const imgAspect = prodImg.width / prodImg.height;
      let sx = 0, sy = 0, sw = prodImg.width, sh = prodImg.height;
      if (imgAspect > 1) { sw = prodImg.height; sx = (prodImg.width - sw) / 2; }
      else if (imgAspect < 1) { sh = prodImg.width; sy = (prodImg.height - sh) / 2; }
      ctx.drawImage(prodImg, sx, sy, sw, sh, 0, 0, S, S);

      // 2. Left frosted blob panel — organic shape matching example
      const barH = 148;
      const panelBottom = S - barH;
      const panelW = S * 0.43;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(panelW - 80, 0);
      // Organic right edge curve
      ctx.bezierCurveTo(
        panelW + 60, 0,
        panelW + 80, panelBottom * 0.25,
        panelW - 10, panelBottom * 0.42
      );
      ctx.bezierCurveTo(
        panelW + 70, panelBottom * 0.58,
        panelW + 50, panelBottom * 0.78,
        panelW - 50, panelBottom
      );
      ctx.lineTo(0, panelBottom);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.80)';
      ctx.fill();
      ctx.restore();

      // 3. Feature icons
      const activeFeatures = features.filter(f => f.trim());
      const totalFeats = Math.min(activeFeatures.length, 4);

      if (totalFeats > 0) {
        const topPad = 160;
        const availH = panelBottom - topPad - 20;
        const gap = availH / totalFeats;

        for (let i = 0; i < totalFeats; i++) {
          const feat = activeFeatures[i];
          const icon = getIconForFeature(feat);
          const centerY = topPad + i * gap + gap / 2;
          const circleX = 68;
          const r = 44;

          // White circle with light border
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(circleX, centerY, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#cccccc';
          ctx.lineWidth = 2;
          ctx.stroke();

          // Icon emoji
          ctx.font = '40px Arial';
          ctx.textAlign = 'center';
          ctx.fillStyle = '#000000';
          ctx.fillText(icon, circleX, centerY + 14);

          // Feature label — bold, to the right of circle
          ctx.fillStyle = '#1a1a1a';
          ctx.font = 'bold 30px Arial';
          ctx.textAlign = 'left';
          ctx.fillText(feat, circleX + r + 18, centerY + 10);

          // Separator (not after last)
          if (i < totalFeats - 1) {
            ctx.strokeStyle = 'rgba(0,0,0,0.08)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(20, centerY + gap / 2);
            ctx.lineTo(panelW - 40, centerY + gap / 2);
            ctx.stroke();
          }
        }
      }

      // 4. Bottom bar OVERLAY — same as Instagram
      const barY = S - barH;
      ctx.fillStyle = 'rgba(248, 248, 248, 0.88)';
      ctx.fillRect(0, barY, S, barH);

      ctx.strokeStyle = 'rgba(180, 180, 180, 0.9)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, barY);
      ctx.lineTo(S, barY);
      ctx.stroke();

      const col2X = S * 0.40;
      const col3X = S * 0.70;

      // Product name
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 34px Arial';
      ctx.textAlign = 'left';
      const truncName = productName.length > 24 ? productName.substring(0, 24) + '...' : productName;
      ctx.fillText(truncName, 32, barY + 52);

      ctx.fillStyle = '#555555';
      ctx.font = '24px Arial';
      ctx.fillText(tagline, 32, barY + 86);

      // Divider 1
      ctx.strokeStyle = 'rgba(180,180,180,0.7)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(col2X - 8, barY + 18);
      ctx.lineTo(col2X - 8, S - 18);
      ctx.stroke();

      // CTA
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(ctaText, col2X, barY + 52);

      ctx.fillStyle = '#555555';
      ctx.font = '22px Arial';
      ctx.fillText(ctaSubtext, col2X, barY + 86);

      // Divider 2
      ctx.strokeStyle = 'rgba(180,180,180,0.7)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(col3X - 8, barY + 18);
      ctx.lineTo(col3X - 8, S - 18);
      ctx.stroke();

      // Amazon logo
      const logoX = col3X + (S - col3X) / 2;
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('amazon', logoX, barY + 52);

      ctx.fillStyle = '#1a1a1a';
      ctx.font = '24px Arial';
      ctx.fillText('.in', logoX, barY + 82);

      ctx.strokeStyle = '#FF9900';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(logoX, barY + 56, 46, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();

      // 5. Logo top left — on frosted panel area
      if (brand?.logoUrl) {
        const logoImg = await loadImage(brand.logoUrl);
        if (logoImg) {
          ctx.drawImage(logoImg, 10, 10, 200, 110);
        }
      } else {
        ctx.fillStyle = '#141414';
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(brand?.brandName || 'Brand', 24, 52);
      }

      setRendered(true);
    };

    draw().catch(err => {
      console.error('Infographic draw error:', err);
      setRendered(true);
    });
  }, [item, brand, productName, tagline, ctaText, ctaSubtext, features]);

  React.useEffect(() => { drawInfographic(); }, [drawInfographic]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'infographic-' + (productName || 'product').replace(/\s+/g, '-').toLowerCase() + '.png';
    link.href = canvas.toDataURL('image/png');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Infographic downloaded!');
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <div className="p-5 border-b border-[#141414] flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileImage className="w-5 h-5" />
            <div>
              <h2 className="font-serif italic text-xl">Infographic</h2>
              <p className="text-[9px] font-mono uppercase opacity-50">Product Feature Showcase</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/50"><XCircle className="w-5 h-5 opacity-50" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="font-mono text-[10px] uppercase tracking-widest opacity-50">Product Features</h3>
              <p className="text-[9px] font-mono opacity-40">Type each feature — icons assigned automatically</p>
              {features.map((feat, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xl w-8 text-center">{feat ? getIconForFeature(feat) : String.fromCharCode(9711)}</span>
                  <input value={feat} onChange={(e) => updateFeature(idx, e.target.value)}
                    placeholder={"Feature " + (idx + 1) + " e.g. Breathable fabric"}
                    className="flex-1 p-2.5 border border-[#141414] text-xs font-mono focus:outline-none bg-transparent" />
                </div>
              ))}
            </div>
            <div className="border-t border-[#141414]/20 pt-3 space-y-2">
              <h3 className="font-mono text-[10px] uppercase tracking-widest opacity-50">Bottom Bar</h3>
              {[
                { label: 'Product Name', val: productName, set: setProductName },
                { label: 'Tagline',      val: tagline,     set: setTagline },
                { label: 'CTA Text',     val: ctaText,     set: setCtaText },
                { label: 'CTA Subtext',  val: ctaSubtext,  set: setCtaSubtext },
              ].map(({ label, val, set }) => (
                <div key={label} className="space-y-1">
                  <p className="text-[9px] uppercase font-mono opacity-50">{label}</p>
                  <input value={val} onChange={(e) => set(e.target.value)}
                    className="w-full p-2 border border-[#141414] text-xs font-mono focus:outline-none bg-transparent" />
                </div>
              ))}
            </div>
            <button onClick={drawInfographic}
              className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-white/50 flex items-center justify-center gap-2">
              <RefreshCw className="w-3 h-3" /> Refresh Preview
            </button>
            <button onClick={handleDownload} disabled={!rendered}
              className="w-full bg-[#141414] text-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 disabled:opacity-30 flex items-center justify-center gap-2">
              <Download className="w-3 h-3" /> Download Infographic
            </button>
            {!rendered && (
              <p className="text-[9px] font-mono opacity-50 text-center">Rendering preview — download activates shortly...</p>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-[9px] uppercase font-mono opacity-50">Preview</p>
            <div className="border border-[#141414] overflow-hidden bg-white">
              <canvas ref={canvasRef} className="w-full h-auto" style={{ display: 'block' }} />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]                   = useState(null);
  const [isAuthReady, setIsAuthReady]     = useState(false);
  const [brand, setBrand]                 = useState(null);
  const [showBrandSetup, setShowBrandSetup] = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [showInstagram, setShowInstagram] = useState(false);
  const [showInfographic, setShowInfographic] = useState(false);
  const [items, setItems]                 = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [viewMode, setViewMode]           = useState('OPTIMIZED');
  const itemsRef                          = useRef([]);

  const setItemsWithRef = useCallback((updater) => {
    setItems((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      itemsRef.current = next;
      return next;
    });
  }, []);

  const compressImage = useCallback((base64Str, maxWidth = 1024, maxHeight = 1024) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > height) {
          if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
        } else {
          if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => resolve(base64Str);
    });
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => { // eslint-disable-next-line react-hooks/exhaustive-deps
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setIsAuthReady(true);
      if (currentUser) {
        const userDocRef = doc(db, 'users', currentUser.uid);
        try {
          const userDoc = await getDoc(userDocRef);
          if (!userDoc.exists()) {
            await setDoc(userDocRef, {
              uid: currentUser.uid, email: currentUser.email,
              displayName: currentUser.displayName, photoURL: currentUser.photoURL,
              createdAt: serverTimestamp(), lastLogin: serverTimestamp(),
            });
            setShowBrandSetup(true); // first time — show brand setup
          } else {
            await updateDoc(userDocRef, { lastLogin: serverTimestamp() });
            const data = userDoc.data();
            if (data.brand) setBrand(data.brand);
            else setShowBrandSetup(true); // no brand set yet
          }
        } catch (err) { console.error('Auth setup error:', err); }
      } else {
        setItemsWithRef([]); setSelectedIndex(null); setBrand(null);
      }
    });
    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async () => {
    try { await signInWithPopup(auth, googleProvider); toast.success('Signed in!'); }
    catch { toast.error('Sign in failed.'); }
  };

  const handleLogout = async () => {
    try { await signOut(auth); toast.info('Signed out.'); }
    catch (err) { console.error(err); }
  };

  // ── Firestore Sync ────────────────────────────────────────────────────────
  useEffect(() => { // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!user || !isAuthReady) return;
    let unsubscribe;
    const setupListener = () => {
      unsubscribe = onSnapshot(
        collection(db, 'users', user.uid, 'batches'),
        (snapshot) => {
          const firestoreItems = snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
          const sorted = firestoreItems.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
          // Preserve full-res images from local state — Firestore only has compressed versions
          setItemsWithRef(prev => sorted.map(fsItem => {
            const local = prev.find(p => p.id === fsItem.id);
            return local ? {
              ...fsItem,
              processedImage: local.processedImage || fsItem.processedImage,
              lifestyleImage: local.lifestyleImage || fsItem.lifestyleImage,
              originalImage:  local.originalImage  || fsItem.originalImage,
            } : fsItem;
          }));
        },
        (err) => {
          console.error('Firestore sync error:', err);
          // Auto-reconnect after 3 seconds on error
          setTimeout(() => {
            console.log('Reconnecting Firestore listener...');
            if (unsubscribe) unsubscribe();
            setupListener();
          }, 3000);
        }
      );
    };
    setupListener();
    return () => unsubscribe();
  }, [user, isAuthReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Update item ───────────────────────────────────────────────────────────
  const updateItem = useCallback(async (id, updates) => { // eslint-disable-line react-hooks/exhaustive-deps
    setItemsWithRef((prev) => prev.map((item) => item.id === id ? { ...item, ...updates } : item));
    if (!user) return;
    try {
      const firestoreUpdates = { ...updates };
      // Strip originalImage from Firestore updates — too large, kept in local state only
      delete firestoreUpdates.originalImage;
      // Compress processed and lifestyle images aggressively before saving
      // Compress images before saving to Firestore (1MB limit)
      // Use same field names so Firestore sync works correctly
      if (firestoreUpdates.processedImage?.length > 700000) {
        firestoreUpdates.processedImage = await compressImage(firestoreUpdates.processedImage, 900, 900);
        if (firestoreUpdates.processedImage?.length > 900000)
          firestoreUpdates.processedImage = await compressImage(firestoreUpdates.processedImage, 700, 700);
      }
      if (firestoreUpdates.lifestyleImage?.length > 700000) {
        firestoreUpdates.lifestyleImage = await compressImage(firestoreUpdates.lifestyleImage, 900, 900);
        if (firestoreUpdates.lifestyleImage?.length > 900000)
          firestoreUpdates.lifestyleImage = await compressImage(firestoreUpdates.lifestyleImage, 700, 700);
      }
      await updateDoc(doc(db, 'users', user.uid, 'batches', id), firestoreUpdates);
    } catch (err) { console.error('Update error:', err); }
  }, [user, compressImage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── API Call ──────────────────────────────────────────────────────────────
  const callAPI = async (payload) => {
    const res = await fetch(API_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`Server error: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.message || 'Unknown error');
    return data;
  };

  // ── Process item ──────────────────────────────────────────────────────────
  const processItem = useCallback(async (id, feedback, useAI = false) => {
    const item = itemsRef.current.find((i) => i.id === id);
    if (!item) return;
    updateItem(id, { state: 'EDITING', error: null });
    toast.info('Processing image...');
    // Timer starts automatically via state change
    try {
      const base64 = item.originalImage.includes(',') ? item.originalImage.split(',')[1] : item.originalImage;
      const data = await callAPI({ imageBase64: base64, feedback: feedback || '', useAI: useAI || false });
      if (itemsRef.current.find((i) => i.id === id)?.state === 'CANCELLED') return;
      updateItem(id, { processedImage: data.processedImage, analysis: data.analysis, state: 'REVIEWING', feedback: '' });
      toast.success('Image processed!');
    } catch (err) {
      if (itemsRef.current.find((i) => i.id === id)?.state === 'CANCELLED') return;
      updateItem(id, { state: 'ERROR', error: err.message });
      toast.error('Processing failed: ' + err.message);
    }
  }, [updateItem]);

  // ── Process with OpenAI ─────────────────────────────────────────────────
  const processWithOpenAI = useCallback(async (id, feedback) => {
    const item = itemsRef.current.find((i) => i.id === id);
    if (!item) return;

    updateItem(id, { state: 'EDITING', error: null });
    toast.info('Processing with OpenAI...', { description: 'Using GPT-4o image model.' });

    try {
      const base64 = item.originalImage.includes(',')
        ? item.originalImage.split(',')[1]
        : item.originalImage;

      const data = await callAPI({
        imageBase64: base64,
        mode: 'openai',
        feedback: feedback || '',
      });

      if (itemsRef.current.find((i) => i.id === id)?.state === 'CANCELLED') return;

      updateItem(id, {
        processedImage: data.processedImage,
        state: 'REVIEWING',
        feedback: '',
      });
      toast.success('OpenAI processing complete!');
    } catch (err) {
      if (itemsRef.current.find((i) => i.id === id)?.state === 'CANCELLED') return;
      updateItem(id, { state: 'ERROR', error: err.message });
      toast.error('OpenAI failed: ' + err.message);
    }
  }, [updateItem]);

  const analyzeItem = useCallback(async (id) => { // eslint-disable-line react-hooks/exhaustive-deps
    updateItem(id, { state: 'ANALYZING', error: null });
    await processItem(id);
  }, [updateItem, processItem]);

  // ── Lifestyle ─────────────────────────────────────────────────────────────
  const generateLifestyle = useCallback(async (id) => {
    const item = itemsRef.current.find((i) => i.id === id);
    const sourceImage = item?.processedImage || item?.originalImage;
    if (!sourceImage) { toast.error('No image found.'); return; }
    updateItem(id, { state: 'LIFESTYLE_GENERATING', error: null });
    const engine = item.lifestyleEngine || 'gemini';
    toast.info('Generating lifestyle image with ' + (engine === 'openai' ? 'OpenAI' : 'Gemini') + '...');
    try {
      const base64 = sourceImage.includes(',') ? sourceImage.split(',')[1] : sourceImage;
      const mode = engine === 'openai' ? 'openai_lifestyle' : 'lifestyle';
      const data = await callAPI({ imageBase64: base64, mode, lifestylePrompt: item.lifestylePrompt });
      if (itemsRef.current.find((i) => i.id === id)?.state === 'CANCELLED') return;
      updateItem(id, { lifestyleImage: data.lifestyleImage, state: 'LIFESTYLE_REVIEWING' });
      toast.success('Lifestyle image generated!');
    } catch (err) {
      if (itemsRef.current.find((i) => i.id === id)?.state === 'CANCELLED') return;
      updateItem(id, { state: 'ERROR', error: err.message });
      toast.error('Lifestyle generation failed.');
    }
  }, [updateItem]);

  // ── Batch ─────────────────────────────────────────────────────────────────
  const processAll = async () => {
    if (isBatchProcessing) return;
    setIsBatchProcessing(true);
    for (const item of itemsRef.current.filter((i) => ['IDLE', 'ERROR'].includes(i.state))) {
      const latest = itemsRef.current.find((i) => i.id === item.id);
      if (!latest || latest.state === 'CANCELLED') continue;
      await processItem(item.id, '', false);
    }
    setIsBatchProcessing(false);
  };

  // ── Drop ──────────────────────────────────────────────────────────────────
  const onDrop = useCallback(async (acceptedFiles) => {
    if (!user) { toast.error('Please sign in first.'); return; }
    const remaining = MAX_BATCH - itemsRef.current.length;
    const files = acceptedFiles.slice(0, remaining);
    if (acceptedFiles.length > remaining) toast.warning(`Only ${remaining} slots remaining.`);

    for (const file of files) {
      const reader = new FileReader();
      reader.onload = async () => {
        let imageData = reader.result;
        if (file.size > 750 * 1024) imageData = await compressImage(imageData);
        const itemId = Math.random().toString(36).substr(2, 9);
        const templates = LIFESTYLE_TEMPLATES('Product', '');
        const newItem = {
          id: itemId, uid: user.uid, originalImage: imageData,
          processedImage: null, lifestyleImage: null, analysis: null,
          state: 'IDLE', error: null, feedback: '',
          lifestylePrompt: templates[0].prompt, lifestyleTemplateId: 'professional',
          customPrompt: '', createdAt: new Date(),
        };
        setItemsWithRef((prev) => { const u = [...prev, newItem]; setSelectedIndex(u.length - 1); return u; });
        try {
          await setDoc(doc(db, 'users', user.uid, 'batches', itemId), { ...newItem, createdAt: serverTimestamp() });
        } catch (err) { console.error('Firestore save failed:', err); }
        // No auto-processing — user chooses what to do next
      };
      reader.readAsDataURL(file);
    }
  }, [user, compressImage]); // eslint-disable-line react-hooks/exhaustive-deps

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop, accept: { 'image/*': [] }, multiple: true, noClick: items.length > 0,
  });

  const removeItem = async (id) => {
    if (user) {
      try { await deleteDoc(doc(db, 'users', user.uid, 'batches', id)); }
      catch (err) { console.error('Delete error:', err); }
    }
    setItemsWithRef((prev) => {
      const updated = prev.filter((i) => i.id !== id);
      if (updated.length === 0) setSelectedIndex(null);
      else if (selectedIndex >= updated.length) setSelectedIndex(updated.length - 1);
      return updated;
    });
  };

  const cancelTask = (id) => { updateItem(id, { state: 'CANCELLED' }); toast.info('Cancelled.'); };

  const downloadItem = (id, type = 'OPTIMIZED') => {
    const item = items.find((i) => i.id === id);
    const image = type === 'LIFESTYLE' ? item?.lifestyleImage : item?.processedImage;
    if (!image) { toast.error('Image not ready.'); return; }
    const link = document.createElement('a');
    link.href = image;
    link.download = `${type.toLowerCase()}-${(item?.analysis?.productName || 'product').replace(/\s+/g, '-').toLowerCase()}.png`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    toast.success('Download started!');
  };

  const downloadAll = () => {
    const ready = items.filter((i) => i.processedImage);
    if (!ready.length) { toast.error('No processed images.'); return; }
    ready.forEach((i) => downloadItem(i.id));
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const selectedItem = selectedIndex !== null ? items[selectedIndex] : null;
  const isProcessing = ['ANALYZING', 'EDITING', 'LIFESTYLE_GENERATING'].includes(selectedItem?.state);
  const displayImage = selectedItem
    ? (viewMode === 'LIFESTYLE' && selectedItem.lifestyleImage
        ? selectedItem.lifestyleImage
        : (selectedItem.processedImage || selectedItem.originalImage))
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#E4E3E0] text-[#141414] font-sans selection:bg-[#141414] selection:text-[#E4E3E0]">
      <Toaster position="top-right" richColors />

      {/* History Panel */}
      <HistoryPanel
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        items={items}
        onSelectItem={(item) => {
          const idx = items.findIndex(i => i.id === item.id);
          if (idx >= 0) setSelectedIndex(idx);
        }}
      />

      {/* Brand Setup Modal */}
      {showBrandSetup && user && (
        <BrandSetupModal
          user={user}
          existingBrand={brand}
          onSave={(b) => { setBrand(b); setShowBrandSetup(false); }}
          onClose={brand ? () => setShowBrandSetup(false) : null}
        />
      )}

      {/* Instagram Post Modal */}
      {showInstagram && selectedItem && (
        <InstagramPostGenerator
          item={selectedItem}
          brand={brand}
          onClose={() => setShowInstagram(false)}
        />
      )}

      {/* Infographic Modal */}
      {showInfographic && selectedItem && (
        <InfographicGenerator
          item={selectedItem}
          brand={brand}
          onClose={() => setShowInfographic(false)}
        />
      )}

      {/* ── Header ── */}
      <header className="border-b border-[#141414] p-4 flex justify-between items-center bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#141414] flex items-center justify-center rounded-sm">
            <Camera className="text-[#E4E3E0] w-5 h-5" />
          </div>
          <div>
            <h1 className="font-serif italic text-lg leading-none">Amazon Image Optimizer</h1>
            <p className="text-[9px] uppercase tracking-widest opacity-50 font-mono mt-0.5">Professional E-Commerce Asset Generation</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isAuthReady && user && (
            <>
              <button
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#141414] text-[9px] font-mono uppercase tracking-widest hover:bg-white/50"
                title="View History"
              >
                <ImageIcon className="w-3 h-3" />
                History {items.length > 0 && <span className="bg-[#141414] text-[#E4E3E0] rounded-full px-1.5 py-0.5 text-[7px]">{items.length}</span>}
              </button>
              <button
                onClick={() => setShowBrandSetup(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#141414] text-[9px] font-mono uppercase tracking-widest hover:bg-white/50"
                title="Brand Settings"
              >
                <Edit3 className="w-3 h-3" />
                {brand ? brand.brandName : 'Setup Brand'}
              </button>
              <div className="flex items-center gap-2">
                <div className="flex flex-col items-end">
                  <span className="text-[10px] font-mono font-bold">{user.displayName}</span>
                  <button onClick={handleLogout} className="text-[8px] font-mono uppercase opacity-50 hover:opacity-100 flex items-center gap-1">
                    <LogOut className="w-2 h-2" /> Sign Out
                  </button>
                </div>
                {user.photoURL
                  ? <img src={user.photoURL} alt="Profile" className="w-7 h-7 rounded-full border border-[#141414]" />
                  : <div className="w-7 h-7 rounded-full border border-[#141414] flex items-center justify-center bg-white"><UserIcon className="w-3 h-3 opacity-50" /></div>
                }
              </div>
            </>
          )}
          {isAuthReady && !user && (
            <button onClick={handleLogin} className="flex items-center gap-2 px-3 py-1.5 bg-[#141414] text-[#E4E3E0] text-[10px] font-mono uppercase tracking-widest hover:opacity-90">
              <LogIn className="w-3 h-3" /> Sign In
            </button>
          )}
          <div className="flex items-center gap-2 px-3 py-1 border border-[#141414] rounded-full text-[10px] font-mono">
            <ShieldCheck className="w-3 h-3" /> COMPLIANCE: VERIFIED
          </div>
        </div>
      </header>

      {/* ── Not signed in ── */}
      {isAuthReady && !user ? (
        <div className="flex flex-col items-center justify-center py-24 text-center space-y-8 px-4">
          <div className="max-w-xl space-y-4">
            <h2 className="font-serif italic text-5xl leading-tight">Professional Amazon Assets,<br />Generated in Seconds.</h2>
            <p className="text-xs opacity-60 font-mono uppercase tracking-widest">Sign in to start optimizing your product listings.</p>
          </div>
          <button onClick={handleLogin} className="px-10 py-4 bg-[#141414] text-[#E4E3E0] font-mono uppercase tracking-[0.2em] text-sm hover:scale-105 transition-transform shadow-[6px_6px_0px_#E4E3E0,6px_6px_0px_1px_#141414]">
            Get Started — Sign In with Google
          </button>
          <div className="grid grid-cols-3 gap-10 pt-12 w-full max-w-3xl">
            {[
              { num: '01.', title: 'Batch Upload',     desc: 'Upload up to 10 images at once.' },
              { num: '02.', title: 'AI Processing',    desc: 'Background removal & enhancement.' },
              { num: '03.', title: 'Social Assets',    desc: 'Instagram posts & infographics.' },
            ].map((f) => (
              <div key={f.num} className="space-y-2 text-left">
                <div className="font-serif italic text-3xl opacity-40">{f.num}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest font-bold">{f.title}</div>
                <p className="text-[10px] opacity-50">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (

      <main className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          {/* ── Left Column ── */}
          <div className="lg:col-span-4 space-y-4">

            {/* Batch Queue */}
            <section className="space-y-3">
              <div className="flex justify-between items-center">
                <h2 className="font-serif italic text-xl">Batch Queue</h2>
                <span className="font-mono text-[10px] opacity-50">{items.length}/{MAX_BATCH}</span>
              </div>

              <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                {items.length === 0 ? (
                  <div className="p-6 border border-dashed border-[#141414] opacity-30 text-center font-mono text-[9px] uppercase tracking-widest">
                    Queue Empty — Upload Images Below
                  </div>
                ) : (
                  items.map((item, idx) => (
                    <div
                      key={item.id} onClick={() => setSelectedIndex(idx)}
                      className={cn(
                        'group relative flex items-center gap-2 p-2 border border-[#141414] cursor-pointer transition-all',
                        selectedIndex === idx ? 'bg-white shadow-[3px_3px_0px_#141414]' : 'bg-white/30 hover:bg-white/50'
                      )}
                    >
                      <div className="w-10 h-10 bg-white border border-[#141414]/10 overflow-hidden flex-shrink-0">
                        <img src={item.originalImage} className="w-full h-full object-cover" alt="" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] font-mono uppercase tracking-tighter truncate">
                          {item.analysis?.productName || `Image ${idx + 1}`}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <div className={cn('w-1.5 h-1.5 rounded-full',
                            item.state === 'COMPLETED' ? 'bg-green-500' :
                            item.state === 'ERROR'     ? 'bg-red-500'   :
                            item.state === 'IDLE'      ? 'bg-gray-300'  : 'bg-blue-500 animate-pulse'
                          )} />
                          <span className="text-[8px] font-mono uppercase opacity-50">{item.state}</span>
                        </div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); removeItem(item.id); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 text-red-500 transition-all">
                        <XCircle className="w-3 h-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Upload + controls */}
              <div className="space-y-2">
                <div
                  {...getRootProps()}
                  className={cn(
                    'border border-dashed border-[#141414] p-4 text-center cursor-pointer transition-colors',
                    isDragActive ? 'bg-[#141414]/5' : 'hover:bg-white/50',
                    items.length >= MAX_BATCH && 'opacity-30 pointer-events-none'
                  )}
                >
                  <input {...getInputProps()} />
                  <Upload className="w-4 h-4 mx-auto mb-1 opacity-40" />
                  <p className="text-[9px] font-mono uppercase tracking-widest opacity-50">
                    {items.length >= MAX_BATCH ? 'Queue Full' : 'Drop images or click to upload'}
                  </p>
                </div>

                {items.length > 0 && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={processAll}
                        disabled={isBatchProcessing || !items.some((i) => ['IDLE', 'ERROR'].includes(i.state))}
                        className="bg-[#141414] text-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 disabled:opacity-30 flex items-center justify-center gap-1.5"
                      >
                        {isBatchProcessing ? <><Loader2 className="w-3 h-3 animate-spin" /> Processing...</> : 'Process All'}
                      </button>
                      <button
                        onClick={downloadAll}
                        disabled={!items.some((i) => i.processedImage)}
                        className="border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all disabled:opacity-30"
                      >
                        Download All
                      </button>
                    </div>
                    <div className="flex gap-2">
                      {items.some((i) => i.state === 'COMPLETED') && (
                        <button onClick={() => items.filter((i) => i.state === 'COMPLETED').forEach((i) => removeItem(i.id))}
                          className="flex-1 text-[9px] font-mono uppercase opacity-50 hover:opacity-100 border border-[#141414] py-1.5">
                          Clear Done
                        </button>
                      )}
                      <button onClick={() => { items.forEach((i) => removeItem(i.id)); setSelectedIndex(null); }}
                        className="flex-1 text-[9px] font-mono uppercase opacity-50 hover:opacity-100 border border-[#141414] py-1.5">
                        Clear All
                      </button>
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* ── Action Panels ── */}
            <AnimatePresence mode="wait">

              {/* IDLE — User chooses what to do */}
              {selectedItem && selectedItem.state === 'IDLE' && (
                <motion.section key="idle" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-[#141414] bg-white space-y-4">
                  <h3 className="font-serif italic text-lg">Choose Action</h3>
                  <p className="text-[9px] font-mono opacity-50">Select what you want to do with this image</p>

                  {/* Remove Background */}
                  <div className="space-y-2">
                    <p className="text-[9px] uppercase font-mono opacity-60 font-bold">🔲 Remove Background</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => processItem(selectedItem.id, '', false)}
                        className="border border-[#141414] py-2.5 px-2 font-mono text-[8px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all text-center">
                        Use Gemini
                        <p className="opacity-50 normal-case mt-0.5">Free · Slower</p>
                      </button>
                      <button
                        onClick={() => processWithOpenAI(selectedItem.id, '')}
                        className="border border-[#141414] py-2.5 px-2 font-mono text-[8px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all text-center">
                        Use OpenAI
                        <p className="opacity-50 normal-case mt-0.5">Paid · Reliable</p>
                      </button>
                    </div>
                  </div>

                  {/* Create Lifestyle Image */}
                  <div className="space-y-2">
                    <p className="text-[9px] uppercase font-mono opacity-60 font-bold">🌿 Create Lifestyle Image</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          const templates = LIFESTYLE_TEMPLATES(selectedItem.analysis?.productName || 'Product', selectedItem.analysis?.description || '');
                          updateItem(selectedItem.id, { state: 'LIFESTYLE_PROMPT', lifestylePrompt: templates[0].prompt, lifestyleTemplateId: 'professional', lifestyleEngine: 'gemini' });
                        }}
                        className="border border-[#141414] py-2.5 px-2 font-mono text-[8px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all text-center">
                        Use Gemini
                        <p className="opacity-50 normal-case mt-0.5">Free · Slower</p>
                      </button>
                      <button
                        onClick={() => {
                          const templates = LIFESTYLE_TEMPLATES(selectedItem.analysis?.productName || 'Product', selectedItem.analysis?.description || '');
                          updateItem(selectedItem.id, { state: 'LIFESTYLE_PROMPT', lifestylePrompt: templates[0].prompt, lifestyleTemplateId: 'professional', lifestyleEngine: 'openai' });
                        }}
                        className="border border-[#141414] py-2.5 px-2 font-mono text-[8px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all text-center">
                        Use OpenAI
                        <p className="opacity-50 normal-case mt-0.5">Paid · Reliable</p>
                      </button>
                    </div>
                  </div>
                </motion.section>
              )}

              {selectedItem && ['ANALYZING', 'EDITING'].includes(selectedItem.state) && (
                <motion.section key="processing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-[#141414] bg-white space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <h3 className="font-serif italic text-lg">{selectedItem.state === 'ANALYZING' ? 'Analyzing...' : 'Processing...'}</h3>
                    </div>
                    <button onClick={() => cancelTask(selectedItem.id)} className="p-1 hover:bg-red-50 text-red-500"><XCircle className="w-4 h-4" /></button>
                  </div>
                  <p className="text-[10px] opacity-60 font-mono">AI is working on your product image.</p>
                  <p className="text-[8px] opacity-40 font-mono">Taking too long? Click cancel and try the other option.</p>
                  <ElapsedTimer isRunning={['ANALYZING', 'EDITING'].includes(selectedItem.state)} />
                </motion.section>
              )}

              {selectedItem?.state === 'REVIEWING' && selectedItem.processedImage && (
                <motion.section key="reviewing" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-[#141414] bg-white space-y-4">
                  <h3 className="font-serif italic text-lg">Quality Review</h3>
                  {selectedItem.analysis?.productName && (
                    <div className="p-3 bg-[#E4E3E0]/50 border border-[#141414]/10 space-y-1">
                      <p className="text-[9px] uppercase font-mono opacity-50">Identified Product</p>
                      <p className="font-medium text-xs">{selectedItem.analysis.productName}</p>
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <p className="text-[9px] uppercase font-mono opacity-50">Feedback / Adjustments</p>
                    <textarea value={selectedItem.feedback || ''} onChange={(e) => updateItem(selectedItem.id, { feedback: e.target.value })}
                      placeholder="e.g. Make the product brighter..."
                      className="w-full p-2.5 border border-[#141414] text-[10px] font-mono focus:outline-none min-h-[60px] resize-none bg-transparent" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => processItem(selectedItem.id, selectedItem.feedback)} disabled={!selectedItem.feedback?.trim()}
                      className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] disabled:opacity-30">
                      Request Changes
                    </button>
                    <div className="border-t border-[#141414]/20 pt-2 space-y-1.5">
                      <p className="text-[8px] font-mono uppercase opacity-40 text-center">Not happy with result?</p>
                      <button
                        onClick={() => processItem(selectedItem.id, selectedItem.feedback, true)}
                        className="w-full bg-[#141414] text-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 flex items-center justify-center gap-2">
                        <RefreshCw className="w-3 h-3" /> Try Gemini AI Instead
                      </button>
                      <button
                        onClick={() => processWithOpenAI(selectedItem.id, selectedItem.feedback)}
                        className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] flex items-center justify-center gap-2">
                        <RefreshCw className="w-3 h-3" /> Try with OpenAI
                      </button>
                      <p className="text-[8px] font-mono opacity-30 text-center">Gemini = free · OpenAI = paid but reliable</p>
                    </div>
                    <button onClick={() => updateItem(selectedItem.id, { state: 'COMPLETED' })}
                      className="w-full bg-[#141414] text-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 flex items-center justify-center gap-2">
                      Approve Asset <ArrowRight className="w-3 h-3" />
                    </button>
                    <button onClick={() => {
                      const templates = LIFESTYLE_TEMPLATES(selectedItem.analysis?.productName || 'Product', selectedItem.analysis?.description || '');
                      updateItem(selectedItem.id, { state: 'LIFESTYLE_PROMPT', lifestylePrompt: templates[0].prompt, lifestyleTemplateId: 'professional' });
                    }} className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] flex items-center justify-center gap-2">
                      Generate Lifestyle <Settings2 className="w-3 h-3" />
                    </button>
                  </div>
                </motion.section>
              )}

              {selectedItem?.state === 'LIFESTYLE_PROMPT' && (
                <motion.section key="lifestyle-prompt" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-[#141414] bg-white space-y-4">
                  <h3 className="font-serif italic text-lg">Lifestyle Style</h3>
                  <div className="space-y-1.5">
                    {LIFESTYLE_TEMPLATES(selectedItem.analysis?.productName || 'Product', selectedItem.analysis?.description || '').map((t) => (
                      <button key={t.id}
                        onClick={() => updateItem(selectedItem.id, t.id === 'custom'
                          ? { lifestyleTemplateId: 'custom', lifestylePrompt: selectedItem.customPrompt || '' }
                          : { lifestyleTemplateId: t.id, lifestylePrompt: t.prompt })}
                        className={cn('w-full p-2.5 text-left border transition-all flex items-center justify-between',
                          selectedItem.lifestyleTemplateId === t.id ? 'border-[#141414] bg-[#141414] text-white' : 'border-gray-200 hover:border-[#141414]')}>
                        <span className="text-[9px] font-mono uppercase tracking-widest">{t.label}</span>
                        {selectedItem.lifestyleTemplateId === t.id && <Check className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                  {selectedItem.lifestyleTemplateId === 'custom' && (
                    <div className="space-y-1.5">
                      <p className="text-[9px] uppercase font-mono opacity-50">Your Custom Prompt</p>
                      <textarea value={selectedItem.customPrompt || ''}
                        onChange={(e) => updateItem(selectedItem.id, { customPrompt: e.target.value, lifestylePrompt: e.target.value })}
                        placeholder="Describe the lifestyle scene..."
                        className="w-full p-2.5 border border-[#141414] text-[10px] font-mono focus:outline-none min-h-[80px] resize-none bg-transparent" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => updateItem(selectedItem.id, { state: 'REVIEWING' })}
                      className="flex-1 border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-gray-50">Back</button>
                    <button onClick={() => generateLifestyle(selectedItem.id)}
                      disabled={selectedItem.lifestyleTemplateId === 'custom' && !selectedItem.customPrompt?.trim()}
                      className="flex-[2] bg-[#141414] text-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-30">
                      Generate <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </motion.section>
              )}

              {selectedItem?.state === 'LIFESTYLE_GENERATING' && (
                <motion.section key="lifestyle-gen" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-[#141414] bg-white space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <h3 className="font-serif italic text-lg">Generating Lifestyle...</h3>
                    </div>
                    <button onClick={() => cancelTask(selectedItem.id)} className="p-1 hover:bg-red-50 text-red-500"><XCircle className="w-4 h-4" /></button>
                  </div>
                  <p className="text-[10px] opacity-60 font-mono">Creating a contextual scene for your product.</p>
                  <ElapsedTimer isRunning={selectedItem.state === 'LIFESTYLE_GENERATING'} />
                </motion.section>
              )}

              {selectedItem?.state === 'LIFESTYLE_REVIEWING' && selectedItem.lifestyleImage && (
                <motion.section key="lifestyle-review" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-[#141414] bg-white space-y-4">
                  <h3 className="font-serif italic text-lg">Lifestyle Review</h3>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => updateItem(selectedItem.id, { state: 'LIFESTYLE_PROMPT' })}
                      className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0]">
                      Try Different Style
                    </button>
                    <button onClick={() => downloadItem(selectedItem.id, 'LIFESTYLE')}
                      className="w-full border border-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] flex items-center justify-center gap-2">
                      <Download className="w-3 h-3" /> Download Lifestyle
                    </button>
                    <button onClick={() => updateItem(selectedItem.id, { state: 'COMPLETED' })}
                      className="w-full bg-[#141414] text-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 flex items-center justify-center gap-2">
                      Approve & Finish <CheckCircle2 className="w-3 h-3" />
                    </button>
                  </div>
                </motion.section>
              )}

              {selectedItem?.state === 'COMPLETED' && (
                <motion.section key="completed" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-[#141414] bg-[#141414] text-[#E4E3E0] space-y-4">
                  <h3 className="font-serif italic text-lg">Asset Ready</h3>
                  <p className="text-[10px] opacity-70">Optimized to Amazon's technical specifications.</p>
                  <div className="flex flex-col gap-2">
                    <button onClick={() => downloadItem(selectedItem.id, 'OPTIMIZED')}
                      className="w-full bg-[#E4E3E0] text-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 flex items-center justify-center gap-2">
                      <Download className="w-3 h-3" /> Download Studio Asset
                    </button>
                    {selectedItem.lifestyleImage && (
                      <button onClick={() => downloadItem(selectedItem.id, 'LIFESTYLE')}
                        className="w-full bg-[#E4E3E0] text-[#141414] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:opacity-90 flex items-center justify-center gap-2">
                        <Download className="w-3 h-3" /> Download Lifestyle
                      </button>
                    )}
                    {!selectedItem.lifestyleImage && (
                      <button onClick={() => {
                        const templates = LIFESTYLE_TEMPLATES(selectedItem.analysis?.productName || 'Product', selectedItem.analysis?.description || '');
                        updateItem(selectedItem.id, { state: 'LIFESTYLE_PROMPT', lifestylePrompt: templates[0].prompt, lifestyleTemplateId: 'professional' });
                      }} className="w-full border border-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#E4E3E0] hover:text-[#141414] flex items-center justify-center gap-2">
                        Generate Lifestyle <ImageIcon className="w-3 h-3" />
                      </button>
                    )}

                    {/* ── NEW: Social Asset Buttons ── */}
                    <div className="border-t border-[#E4E3E0]/20 pt-2 space-y-2">
                      <p className="text-[8px] font-mono uppercase opacity-50 text-center">Social Assets</p>
                      <button
                        onClick={() => {
                          if (!brand) { toast.error('Please set up your brand first.'); setShowBrandSetup(true); return; }
                          if (!selectedItem.lifestyleImage) { toast.error('Generate a lifestyle image first.'); return; }
                          setShowInstagram(true);
                        }}
                        className="w-full border border-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#E4E3E0] hover:text-[#141414] flex items-center justify-center gap-2"
                      >
                        <Share2 className="w-3 h-3" /> Create Instagram Post
                      </button>
                      <button
                        onClick={() => {
                          if (!brand) { toast.error('Please set up your brand first.'); setShowBrandSetup(true); return; }
                          setShowInfographic(true);
                        }}
                        className="w-full border border-[#E4E3E0] py-2.5 font-mono text-[9px] uppercase tracking-widest hover:bg-[#E4E3E0] hover:text-[#141414] flex items-center justify-center gap-2"
                      >
                        <FileImage className="w-3 h-3" /> Create Infographic
                      </button>
                    </div>
                  </div>
                </motion.section>
              )}

              {selectedItem?.state === 'ERROR' && (
                <motion.section key="error" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-red-500 bg-red-50 text-red-900 space-y-3">
                  <div className="flex items-center gap-2"><AlertCircle className="w-4 h-4" /><h3 className="font-serif italic text-lg">Error</h3></div>
                  <p className="text-[10px] font-mono opacity-80">{selectedItem.error}</p>
                  <button onClick={() => processItem(selectedItem.id)} className="w-full bg-red-900 text-white py-2.5 font-mono text-[9px] uppercase tracking-widest">Retry</button>
                </motion.section>
              )}

              {selectedItem?.state === 'CANCELLED' && (
                <motion.section key="cancelled" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                  className="p-5 border border-gray-400 bg-gray-50 text-gray-600 space-y-3">
                  <h3 className="font-serif italic text-lg">Cancelled</h3>
                  <button onClick={() => updateItem(selectedItem.id, { state: 'IDLE' })} className="w-full bg-gray-800 text-white py-2.5 font-mono text-[9px] uppercase tracking-widest">Restart</button>
                </motion.section>
              )}

            </AnimatePresence>
          </div>

          {/* ── Right Column: Viewport ── */}
          <div className="lg:col-span-8">
            <div className="w-full relative" style={{ paddingBottom: '100%' }}>
              <div className="absolute inset-0 bg-white border border-[#141414] overflow-hidden">
                <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                  style={{ backgroundImage: 'linear-gradient(#141414 1px, transparent 1px), linear-gradient(90deg, #141414 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                <AnimatePresence mode="wait">
                  {!selectedItem && (
                    <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      {...getRootProps()}
                      className={cn('absolute inset-0 flex flex-col items-center justify-center cursor-pointer transition-colors p-12 text-center',
                        isDragActive ? 'bg-[#141414]/5' : 'hover:bg-[#141414]/[0.02]')}>
                      <input {...getInputProps()} />
                      <div className="w-20 h-20 border border-dashed border-[#141414] flex items-center justify-center mb-6">
                        <Upload className="w-8 h-8 opacity-40" />
                      </div>
                      <h3 className="font-serif italic text-3xl mb-2">Upload Product Images</h3>
                      <p className="text-xs font-mono opacity-50 uppercase tracking-widest">Drag & Drop or Click — Up to 10 Images</p>
                      <div className="mt-8 flex gap-4 text-[10px] font-mono opacity-40 uppercase tracking-tighter">
                        <span>JPEG</span><span>•</span><span>PNG</span><span>•</span><span>WEBP</span><span>•</span><span>TIFF</span>
                      </div>
                    </motion.div>
                  )}

                  {selectedItem && (
                    <motion.div key={selectedItem.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="absolute inset-0 flex items-center justify-center p-6">
                      <div className="relative w-full h-full flex items-center justify-center">
                        <img src={displayImage} alt="Product Preview"
                          className={cn('max-w-full max-h-full object-contain transition-all duration-700',
                            isProcessing ? 'blur-sm opacity-50 scale-95' : 'scale-100')}
                          referrerPolicy="no-referrer" />

                        {isProcessing && (
                          <motion.div initial={{ top: '0%' }} animate={{ top: '100%' }}
                            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                            className="absolute left-0 right-0 h-[2px] bg-[#141414] z-10 shadow-[0_0_15px_rgba(20,20,20,0.5)]" />
                        )}

                        <div className="absolute top-0 left-0 flex items-center gap-2">
                          <div className="bg-[#141414] text-[#E4E3E0] px-3 py-1 text-[9px] font-mono uppercase tracking-widest">
                            {selectedItem.state === 'COMPLETED'           ? 'Optimized Asset'  :
                             selectedItem.state === 'REVIEWING'           ? 'Review Draft'     :
                             selectedItem.state === 'LIFESTYLE_REVIEWING' ? 'Lifestyle Draft'  :
                             isProcessing                                 ? 'Processing...'   : 'Source Preview'}
                          </div>
                          {selectedItem.lifestyleImage && (
                            <div className="flex border border-[#141414]">
                              {['OPTIMIZED', 'LIFESTYLE'].map((mode) => (
                                <button key={mode} onClick={() => setViewMode(mode)}
                                  className={cn('px-3 py-1 text-[8px] font-mono uppercase tracking-widest transition-colors',
                                    viewMode === mode ? 'bg-[#141414] text-[#E4E3E0]' : 'bg-white text-[#141414]')}>
                                  {mode === 'OPTIMIZED' ? 'Studio' : 'Lifestyle'}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {['COMPLETED', 'REVIEWING'].includes(selectedItem.state) && (
                          <div className="absolute bottom-3 right-3 flex gap-2">
                            <div className="bg-white/90 border border-[#141414] px-2.5 py-1 text-[9px] font-mono uppercase flex items-center gap-1.5">
                              <Maximize2 className="w-2.5 h-2.5" /> 2000px+
                            </div>
                            <div className="bg-white/90 border border-[#141414] px-2.5 py-1 text-[9px] font-mono uppercase flex items-center gap-1.5">
                              <ImageIcon className="w-2.5 h-2.5" /> RGB 255,255,255
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {items.length > 0 && items.length < MAX_BATCH && (
                  <div className="absolute bottom-4 left-4">
                    <button onClick={open}
                      className="bg-white/90 border border-[#141414] px-3 py-1.5 text-[9px] font-mono uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all flex items-center gap-1.5 shadow-[3px_3px_0px_#141414]">
                      <Plus className="w-3 h-3" /> Add More
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Spec bar */}
            <div className="mt-4 grid grid-cols-3 gap-4">
              {[
                { label: 'Background', value: 'Pure White (255,255,255)', icon: ImageIcon },
                { label: 'Resolution', value: '2000px Longest Side',      icon: Maximize2 },
                { label: 'Focus',      value: '85% Frame Occupancy',      icon: RefreshCw },
              ].map((spec) => (
                <div key={spec.label} className="p-3 border border-[#141414] bg-white/50 space-y-0.5">
                  <div className="flex items-center gap-1.5 opacity-50">
                    <spec.icon className="w-2.5 h-2.5" />
                    <span className="text-[8px] uppercase font-mono tracking-widest">{spec.label}</span>
                  </div>
                  <p className="text-[10px] font-medium">{spec.value}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>
      )}

      {/* ── Footer ── */}
      <footer className="mt-16 border-t border-[#141414] p-10 bg-white/30">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="space-y-3">
            <h4 className="font-serif italic text-base">About the Optimizer</h4>
            <p className="text-[10px] opacity-60 leading-relaxed">Powered by Gemini AI — background removal, enhancement, lifestyle images, Instagram posts and infographics.</p>
          </div>
          <div className="space-y-3">
            <h4 className="font-serif italic text-base">Technical Compliance</h4>
            <ul className="text-[9px] font-mono space-y-1.5 uppercase tracking-tighter opacity-60">
              <li>• RGB 255, 255, 255 Background</li>
              <li>• No Watermarks or Text Overlays</li>
              <li>• Realistic Color Representation</li>
              <li>• Sharp Focus & High Detail</li>
            </ul>
          </div>
          <div className="space-y-3">
            <h4 className="font-serif italic text-base">System Status</h4>
            <div className="flex items-center gap-2 text-[9px] font-mono"><div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />AI ENGINE: OPERATIONAL</div>
            <div className="flex items-center gap-2 text-[9px] font-mono"><div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />BACKEND: CLOUD FUNCTION</div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-10 border-t border-[#141414]/10 flex justify-between items-center text-[8px] font-mono uppercase opacity-40">
          <span>© 2026 Amazon Image Optimizer</span>
          <span>enhanceimage.5why.in</span>
        </div>
      </footer>
    </div>
  );
}